import type { ReactNode } from "react";

import { CommandGroup } from "@/components/ui/command";
import { Skeleton } from "@/components/ui/skeleton";
import type { MarketSearchHit } from "@/pages/market-intelligence/market-search";
import type { CustomReportSummary } from "@/types/reports";
import type { SearchResult } from "@/types/search";

import { MarketSearchRow } from "./MarketSearchRow";
import { ReportSearchRow } from "./ReportSearchRow";
import { SearchResultRow } from "./SearchResultRow";

interface SearchResultsListProps {
  results: SearchResult[];
  /** Market Intelligence destinations, computed client-side (`useMarketSearch`). */
  marketHits?: MarketSearchHit[];
  /** The caller's own custom reports matching the term (`useReportSearch`). */
  reportHits?: CustomReportSummary[];
  /**
   * Records and Market Intelligence side by side (the desktop dropdown), or
   * stacked (the mobile sheet, too narrow for two).
   */
  columns?: boolean;
  /** True only once the current term's query has settled with no hits. */
  settledEmpty: boolean;
  /** The (trimmed) term, for the empty-state message. */
  query: string;
  onSelect: (r: SearchResult) => void;
  onSelectMarket?: (hit: MarketSearchHit) => void;
  onSelectReport?: (report: CustomReportSummary) => void;
}

/** A group heading, styled as `CommandGroup`'s, for a column holding no items. */
const HEADING_CLASS =
  "px-3 py-1.5 text-xs font-semibold uppercase tracking-eyebrow text-text-dim";

const GROUP_CLASS = "p-0 [&_[cmdk-group-heading]]:px-3";

/** Each column scrolls on its own, so a long list on one side never moves the other. */
const COLUMN_CLASS = "scrollbar-thin max-h-[70vh] min-w-0 overflow-y-auto py-1";

function LoadingRows() {
  return (
    <div className="space-y-2 p-3">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-5 w-2/3" />
      <Skeleton className="h-5 w-1/2" />
    </div>
  );
}

/**
 * The contents of a cmdk `CommandList` for both surfaces: ranked rows
 * (`CommandItem`s via {@link SearchResultRow}), a settled "no matches" state,
 * or — by default — a loading skeleton (never the empty state while a fetch is
 * in flight, so it doesn't flash between skeleton and results).
 *
 * **With Market Intelligence hits, the desktop dropdown has two columns:**
 * records on the left, where they always were, and MI on the right. Stacked,
 * a point with five views pushed every client and deal below the fold. Each
 * column keeps its own loading and empty state, so records still loading
 * beside an instant MI answer reads as exactly that. cmdk's arrow keys walk
 * the DOM, so they run down the records and then on into Market Intelligence.
 *
 * Stacked (mobile), MI comes first: it is computed in the browser, so it is
 * there on the first keystroke and the server's records arrive beneath it
 * without moving it.
 */
export function SearchResultsList({
  results,
  marketHits = [],
  reportHits = [],
  columns = false,
  settledEmpty,
  query,
  onSelect,
  onSelectMarket,
  onSelectReport,
}: SearchResultsListProps) {
  // The right-hand column holds everything matched in the browser: Market
  // Intelligence destinations and the caller's own reports.
  const hasMarket = marketHits.length > 0 || reportHits.length > 0;

  const records =
    results.length > 0
      ? results.map((r, i) => {
          const value = `${r.kind}:${r.id}:${i}`;
          return (
            <SearchResultRow key={value} value={value} result={r} onSelect={() => onSelect(r)} />
          );
        })
      : null;

  const market = (
    <>
      {marketHits.length > 0 && (
        <CommandGroup heading="Market Intelligence" className={GROUP_CLASS}>
          {marketHits.map((hit) => {
            const value = `mi:${hit.key}`;
            return (
              <MarketSearchRow
                key={value}
                value={value}
                hit={hit}
                onSelect={() => onSelectMarket?.(hit)}
              />
            );
          })}
        </CommandGroup>
      )}
      {reportHits.length > 0 && (
        <CommandGroup heading="Reports" className={GROUP_CLASS}>
          {reportHits.map((report) => {
            const value = `report:${report.id}`;
            return (
              <ReportSearchRow
                key={value}
                value={value}
                report={report}
                onSelect={() => onSelectReport?.(report)}
              />
            );
          })}
        </CommandGroup>
      )}
    </>
  );

  if (hasMarket && columns) {
    let recordsColumn: ReactNode;
    if (records) {
      recordsColumn = (
        <CommandGroup heading="Records" className={GROUP_CLASS}>
          {records}
        </CommandGroup>
      );
    } else {
      recordsColumn = (
        <>
          <div className={HEADING_CLASS}>Records</div>
          {settledEmpty ? (
            <div className="px-3 py-4 text-sm text-muted-foreground">
              No records match &ldquo;{query}&rdquo;
            </div>
          ) : (
            <LoadingRows />
          )}
        </>
      );
    }
    return (
      <div className="grid grid-cols-2 divide-x divide-border">
        <div className={COLUMN_CLASS}>{recordsColumn}</div>
        <div className={COLUMN_CLASS}>{market}</div>
      </div>
    );
  }

  if (hasMarket) {
    return (
      <>
        {market}
        {records && (
          <CommandGroup heading="Records" className={GROUP_CLASS}>
            {records}
          </CommandGroup>
        )}
      </>
    );
  }

  if (records) {
    return <>{records}</>;
  }
  if (settledEmpty) {
    return (
      <div className="px-3 py-6 text-center text-sm text-muted-foreground">
        No matches for &ldquo;{query}&rdquo;
      </div>
    );
  }
  return <LoadingRows />;
}
